
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { ISortParameters } from "../../common/models/ISortParameters";
import restService from "../rest-service/RestService";
import { IAlarmAndRecipientsSetpointInformation, IAlarmInformation, IBadComms, IDisabledRecipient, IRecipientNotification, ISetPointListReportModel } from "./Models";


interface IAlarmNotificationRestQueryParams {
    startDate?: Date;
    endDate?: Date;
}

interface IBadCommsRestQueryParams extends IAlarmNotificationRestQueryParams {

}

interface IDisabledRecipientsQueryParams extends IAlarmNotificationRestQueryParams {

}

interface IAlarmNotificationExportRestQueryParams {
    notificationKey: string;
    mimeType: ApplicationMimeTypesEnum;
    sortParameters: ISortParameters;
    groupID: number;
}

interface IAlarmSetpointReportQueryParams {
    contextGroupID?: number;
    organizationID: number;
    includeInActiveAndInvisibleDevices: boolean
}

interface IAlarmSetpointReportExportQueryParams extends IAlarmSetpointReportQueryParams {
    notificationKey: string;
    mimeType: ApplicationMimeTypesEnum;
    sortParameters: ISortParameters;
}


interface IBadCommsExportQueryParams extends IBadCommsRestQueryParams {
    notificationKey: string;
    mimeType: ApplicationMimeTypesEnum;
    sortParameters: ISortParameters;
    groupId: number;

};




const AdminReportDataService = {

    getAlarmNotifications: async function (
        contextGroupID: number,
        startDate: Date | undefined,
        endDate: Date | undefined,
        signal?: AbortSignal
    ): Promise<IAlarmInformation[]> {

        const path = `groups/${contextGroupID}/getalarmnotifications`;
        const queryParams = {
            startDate: startDate,
            endDate: endDate
        } as IAlarmNotificationRestQueryParams;
        
        const response = await restService.get<IAlarmInformation[], IAlarmNotificationRestQueryParams>
                                        (path, signal, queryParams);

        return response.data;
    },

    exportAlarmNotifications: async function(
        notificationKey: string,
        contextGroupID: number, 
        startDate: Date | undefined, 
        endDate: Date | undefined, 
        mimeType: ApplicationMimeTypesEnum, 
        sortParameters: ISortParameters
    ) : Promise<void> {

        const path = `groups/${contextGroupID}/getalarmnotifications/export`;

        const queryParams = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            sortParameters: sortParameters,
            groupID: contextGroupID,
            startDate: startDate,
            endDate: endDate
        } as IAlarmNotificationExportRestQueryParams;
        
        await restService.post<IAlarmNotificationExportRestQueryParams, void>
                                        (path, queryParams);
    },


    getAlarmSetpointReport: async function (
        organizationID: number, 
        contextGroupID: number,
        includeInActiveAndInvisibleDevices: boolean,
        signal?: AbortSignal
    ): Promise<IAlarmAndRecipientsSetpointInformation[]> {

        const path = `groups/${contextGroupID}/getalarmandrecipientforsetpoints`;
        const queryParams = {
            organizationID,
            includeInActiveAndInvisibleDevices
        } as IAlarmSetpointReportQueryParams;
        
        const response = await restService.get<IAlarmAndRecipientsSetpointInformation[], IAlarmSetpointReportQueryParams>
                                        (path, signal, queryParams);

        return response.data;
    },
    getAlarmSetpointReportV2: async function (
        organizationID: number, 
        contextGroupID: number,
        includeInActiveAndInvisibleDevices: boolean,
        signal?: AbortSignal
    ): Promise<ISetPointListReportModel> {

        const path = 'admin/reports/setpointlist';
        const queryParams = {
            organizationID,
            contextGroupID,
            includeInActiveAndInvisibleDevices
        } as IAlarmSetpointReportQueryParams;
        
        const response = await restService.get<ISetPointListReportModel, IAlarmSetpointReportQueryParams>(path, signal, queryParams);
        return response.data;
    },

    exportAlarmSetpointReport: async function(
        notificationKey: string,
        organizationID: number, 
        contextGroupID: number, 
        includeInActiveAndInvisibleDevices: boolean,
        mimeType: ApplicationMimeTypesEnum, 
        sortParameters: ISortParameters
    ) : Promise<void> {

        const path = `groups/${contextGroupID}/getalarmandrecipientforsetpoints/export`;


        const queryParams = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            sortParameters: sortParameters,
            organizationID: organizationID,
            includeInActiveAndInvisibleDevices: includeInActiveAndInvisibleDevices
        } as IAlarmSetpointReportExportQueryParams;
        
        await restService.post<IAlarmSetpointReportExportQueryParams, void>
                                        (path, queryParams);
    },
    getBadCommsReport: async function (
        contextGroupID: number,
        startDate: Date,
        endDate: Date,
        signal?: AbortSignal
    ): Promise<IBadComms[]> {

        const path = `groups/${contextGroupID}/getbadcomms`;
        const queryParams = {
            startDate,
            endDate
        } as IBadCommsRestQueryParams;
        
        const response = await restService.get<IBadComms[], IBadCommsRestQueryParams>
                                        (path, signal, queryParams);

        return response.data;
    },

    exportBadCommsReport: async function(
        notificationKey: string,
        contextGroupID: number, 
        startDate: Date,
        endDate: Date,
        mimeType: ApplicationMimeTypesEnum, 
        sortParameters: ISortParameters
    ) : Promise<void> {

        const path = `groups/${contextGroupID}/getbadcomms/export`;

        const queryParams = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            sortParameters: sortParameters,
            startDate: startDate,
            endDate: endDate,
            groupId: contextGroupID
        } as IBadCommsExportQueryParams;
        
        await restService.post<IBadCommsExportQueryParams, void>
                                        (path, queryParams);
    },
    getDisabledRecipients: async function (
        contextGroupID: number,
        startDate: Date,
        endDate: Date,
        signal?: AbortSignal
    ): Promise<IDisabledRecipient[]> {

        const path = `groups/${contextGroupID}/getDisabledRecipients`;
        const queryParams = {
            startDate,
            endDate
        } as IDisabledRecipientsQueryParams;
        
        const response = await restService.get<IDisabledRecipient[], IDisabledRecipientsQueryParams>
                                        (path, signal, queryParams);

        return response.data;
    },

    exportDisabledRecipientsReport: async function(
        notificationKey: string,
        contextGroupID: number, 
        startDate: Date,
        endDate: Date,
        mimeType: ApplicationMimeTypesEnum, 
        sortParameters: ISortParameters
    ) : Promise<void> {

        const path = `groups/${contextGroupID}/getDisabledRecipients/export`;

        const queryParams = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            sortParameters: sortParameters,
            startDate: startDate,
            endDate: endDate,
            groupID: contextGroupID
        } as IAlarmNotificationExportRestQueryParams;
        
        await restService.post<IAlarmNotificationExportRestQueryParams, void>(path, queryParams);
    },
    getAvailableRecipientsForUser: async function (
        contextGroupID: number,
        signal?: AbortSignal
    ): Promise<IRecipientNotification[]> {

        const path = `groups/${contextGroupID}/recipients/getrecipientsforuser`;
        
        const response = await restService.get<IRecipientNotification[]>(path, signal);

        return response.data;
    },
    saveRecipientsToNotifyForUser: async function (
        contextGroupID: number,
        recipients: IRecipientNotification[]
    ): Promise<void> {

        const path = `groups/${contextGroupID}/recipients/saveflaggedrecipients`;
        
        await restService.post<IRecipientNotification[], void>(path, recipients);

    },
};

export default AdminReportDataService;